import api from './api';

/**
 * Simulation API service for traffic and chaos simulation
 */
export const simulationAPI = {
  /**
   * Run traffic simulation on a design
   * @param {Object} designJson - The design graph JSON
   * @param {number[]} componentList - List of component IDs used
   * @param {Object} config - Simulation configuration
   * @param {Object} edgeConfigs - Optional user-defined edge traffic configs
   */
  runSimulation: (designJson, componentList, config = {}, edgeConfigs = null) => {
    const payload = {
      design_json: designJson,
      component_list: componentList,
      config: {
        entry_traffic: config.entryTraffic || {},
        read_write_ratio: config.readWriteRatio || 0.8,
        traffic_pattern: config.trafficPattern || 'steady',
        spike_factor: config.spikeFactor || 1.0,
        max_steps: config.maxSteps || 100,
        time_step_ms: config.timeStepMs || 10,
        enable_animation: config.enableAnimation || true,
      },
    };

    if (edgeConfigs) {
      payload.edge_configs = edgeConfigs;
    }

    return api.post('/simulation/run', payload);
  },

  /**
   * Run chaos/failure simulation
   * @param {Object} designJson - The design graph JSON
   * @param {number[]} componentList - List of component IDs used
   * @param {string} scenarioId - Predefined scenario ID (optional)
   * @param {string[]} customNodes - Custom nodes to fail (optional)
   * @param {string[]} customEdges - Custom edges to fail (optional)
   * @param {boolean} testAllSpofs - Test all nodes as potential SPOFs
   */
  runChaos: (designJson, componentList, options = {}) => {
    return api.post('/simulation/chaos', {
      design_json: designJson,
      component_list: componentList,
      scenario_id: options.scenarioId || '',
      custom_nodes: options.customNodes || [],
      custom_edges: options.customEdges || [],
      test_all_spofs: options.testAllSpofs || false,
    });
  },

  /**
   * Get all predefined chaos scenarios
   */
  getScenarios: () => api.get('/simulation/scenarios'),

  /**
   * Test a single node as a potential SPOF
   * @param {Object} designJson - The design graph JSON
   * @param {number[]} componentList - List of component IDs used
   * @param {string} nodeId - Node ID to test
   */
  testSingleSpof: (designJson, componentList, nodeId) => {
    return api.post('/simulation/spof', {
      design_json: designJson,
      component_list: componentList,
      node_id: nodeId,
    });
  },

  /**
   * Simulate network partition between two groups
   * @param {Object} designJson - The design graph JSON
   * @param {number[]} componentList - List of component IDs used
   * @param {string[]} groupA - First group of node IDs
   * @param {string[]} groupB - Second group of node IDs
   */
  simulatePartition: (designJson, componentList, groupA, groupB) => {
    return api.post('/simulation/partition', {
      design_json: designJson,
      component_list: componentList,
      group_a: groupA,
      group_b: groupB,
    });
  },
};

export default simulationAPI;
